import type pino from "pino";
import { existsSync, mkdirSync, readFileSync, renameSync, writeFileSync } from "node:fs";
import { dirname } from "node:path";
import type { NotificationPreferences } from "../../shared/notification-events.js";
import {
  NotificationPreferencesSchema,
  shouldDeliverEvent,
  type NotificationEventId,
} from "../../shared/notification-events.js";

export interface PushTokenEntry {
  token: string;
  /** Stable per-install id from the client. May be absent for legacy entries. */
  deviceId?: string;
  /**
   * Auth-server userId of the connecting session that registered this token.
   * `null` for legacy entries written before multi-tenant scoping, and for
   * sessions on a daemon without auth-server linkage. Used to gate per-agent
   * push fan-out so a shared host doesn't leak attention events between
   * users.
   */
  userId: string | null;
  /** Per-event preferences. Missing entries fall back to registry defaults. */
  preferences?: NotificationPreferences;
  /** ISO timestamp; updated on every register. */
  updatedAt: string;
}

interface PersistedState {
  /** New shape — entries keyed by token. */
  entries?: PushTokenEntry[];
  /** Legacy v0 shape — bare token strings. Loader migrates these forward. */
  tokens?: string[];
}

/**
 * Store for Expo push tokens.
 *
 * Tokens are persisted to disk so pushes still work after daemon restarts.
 * Each token carries optional `deviceId` + `preferences` so the daemon can
 * filter by per-event toggles set in the client's settings.
 */
export class PushTokenStore {
  private readonly logger: pino.Logger;
  private readonly entries: Map<string, PushTokenEntry> = new Map();
  private readonly filePath: string;

  constructor(logger: pino.Logger, filePath: string) {
    this.logger = logger.child({ component: "token-store" });
    this.filePath = filePath;
    this.loadFromDisk();
  }

  /**
   * Upsert a token registration. If the entry already exists, prefs/deviceId
   * are merged in (latest write wins for each field).
   */
  upsertToken(input: {
    token: string;
    deviceId?: string;
    userId?: string | null;
    preferences?: NotificationPreferences;
  }): void {
    const token = input.token.trim();
    if (!token) return;
    const existing = this.entries.get(token);
    const next: PushTokenEntry = {
      token,
      deviceId: input.deviceId ?? existing?.deviceId,
      userId: input.userId !== undefined ? input.userId : (existing?.userId ?? null),
      preferences: input.preferences ?? existing?.preferences,
      updatedAt: new Date().toISOString(),
    };
    this.entries.set(token, next);
    this.persist();
    this.logger.debug(
      { total: this.entries.size, deviceId: next.deviceId, userId: next.userId },
      "Upserted token",
    );
  }

  /** Replace prefs on the entry matching `deviceId` or `token`. No-op if absent. */
  updatePreferences(args: {
    deviceId?: string;
    token?: string;
    preferences: NotificationPreferences;
  }): boolean {
    const entry = this.findByDeviceOrToken(args);
    if (!entry) return false;
    entry.preferences = args.preferences;
    entry.updatedAt = new Date().toISOString();
    this.persist();
    return true;
  }

  removeToken(token: string): void {
    const normalized = token.trim();
    if (!normalized) return;
    if (this.entries.delete(normalized)) {
      this.persist();
      this.logger.debug({ total: this.entries.size }, "Removed token");
    }
  }

  /** Drop every entry for a given device id (e.g. user signed out on phone). */
  removeByDeviceId(deviceId: string): number {
    let removed = 0;
    for (const [token, entry] of this.entries) {
      if (entry.deviceId === deviceId) {
        this.entries.delete(token);
        removed += 1;
      }
    }
    if (removed > 0) this.persist();
    return removed;
  }

  /** Tokens whose prefs say they want this event (or have no prefs at all). */
  getTokensForEvent(eventId: NotificationEventId): string[] {
    const out: string[] = [];
    for (const entry of this.entries.values()) {
      if (shouldDeliverEvent(entry.preferences, eventId)) {
        out.push(entry.token);
      }
    }
    return out;
  }

  /**
   * Same as `getTokensForEvent` but additionally filters by which user is
   * allowed to receive notifications for the agent that triggered the event.
   *
   * Visibility rules:
   *   - Agents with `ownerUserId === null` are unmigrated/corrupt and MUST
   *     receive zero pushes (fail closed).
   *   - An entry receives the push iff `entry.userId === agent.ownerUserId`.
   *   - Legacy entries (`entry.userId === null`) are treated as unknown and
   *     dropped — the user must re-register the token from a session that
   *     the daemon can resolve to a userId.
   */
  getTokensForEventAndAgent(
    eventId: NotificationEventId,
    agent: { ownerUserId: string | null },
  ): string[] {
    if (agent.ownerUserId === null) return [];
    const ownerUserId: string = agent.ownerUserId;
    const out: string[] = [];
    for (const entry of this.entries.values()) {
      if (!shouldDeliverEvent(entry.preferences, eventId)) continue;
      if (entry.userId === null) continue;
      if (entry.userId === ownerUserId) {
        out.push(entry.token);
      }
    }
    return out;
  }

  /** Tokens for a single device (test push, unregister-all-on-this-device, etc). */
  getTokensForDevice(deviceId: string): string[] {
    const out: string[] = [];
    for (const entry of this.entries.values()) {
      if (entry.deviceId === deviceId) out.push(entry.token);
    }
    return out;
  }

  getAllTokens(): string[] {
    return Array.from(this.entries.keys());
  }

  getAllEntries(): PushTokenEntry[] {
    return Array.from(this.entries.values());
  }

  private findByDeviceOrToken(args: {
    deviceId?: string;
    token?: string;
  }): PushTokenEntry | undefined {
    if (args.token) {
      const direct = this.entries.get(args.token.trim());
      if (direct) return direct;
    }
    if (args.deviceId) {
      for (const entry of this.entries.values()) {
        if (entry.deviceId === args.deviceId) return entry;
      }
    }
    return undefined;
  }

  private loadFromDisk(): void {
    try {
      if (!existsSync(this.filePath)) {
        return;
      }
      const raw = readFileSync(this.filePath, "utf-8");
      const parsed = JSON.parse(raw) as PersistedState;

      if (Array.isArray(parsed.entries)) {
        for (const e of parsed.entries) {
          if (!e || typeof e.token !== "string" || !e.token.trim()) continue;
          const prefs = NotificationPreferencesSchema.optional().safeParse(e.preferences);
          this.entries.set(e.token.trim(), {
            token: e.token.trim(),
            deviceId: typeof e.deviceId === "string" ? e.deviceId : undefined,
            userId: typeof e.userId === "string" ? e.userId : null,
            preferences: prefs.success ? prefs.data : undefined,
            updatedAt: typeof e.updatedAt === "string" ? e.updatedAt : new Date().toISOString(),
          });
        }
      } else if (Array.isArray(parsed.tokens)) {
        // Legacy v0 — migrate forward. No userId available; entries land as
        // null and are excluded from owned-agent fan-out until re-registered.
        const now = new Date().toISOString();
        for (const t of parsed.tokens) {
          if (typeof t !== "string" || !t.trim()) continue;
          this.entries.set(t.trim(), { token: t.trim(), userId: null, updatedAt: now });
        }
        this.logger.info(
          { migrated: this.entries.size },
          "Migrated legacy push token format to entries",
        );
        this.persist();
      }

      this.logger.info({ total: this.entries.size }, "Loaded push tokens");
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.warn({ err }, "Failed to load push tokens");
    }
  }

  private persist(): void {
    try {
      mkdirSync(dirname(this.filePath), { recursive: true });
      const tmpPath = `${this.filePath}.tmp`;
      const payload: PersistedState = { entries: Array.from(this.entries.values()) };
      writeFileSync(tmpPath, JSON.stringify(payload, null, 2) + "\n");
      renameSync(tmpPath, this.filePath);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.warn({ err }, "Failed to persist push tokens");
    }
  }
}
